import React, { useState } from 'react';
import { UsersIcon, BriefcaseIcon, HeartHandshakeIcon, CheckCircleIcon, FileTextIcon, CreditCardIcon, UserIcon, BuildingIcon, CalculatorIcon, HomeIcon, ArrowRightLeftIcon, LeafIcon } from 'lucide-react';
import { FadeIn } from '../components/FadeIn';
import { SectionHeading } from '../components/SectionHeading';
import { AccordionItem } from '../components/AccordionItem';
import { PageHero } from '../components/PageHero';
import { ProcessTimeline } from '../components/ProcessTimeline';
import { CTASection } from '../components/CTASection';
import { Button } from '../components/Button';
interface HypothekenPageProps {
  onNavigate: (page: string) => void;
}
export function HypothekenPage({
  onNavigate
}: HypothekenPageProps) {
  const [openAccordion, setOpenAccordion] = useState<number | null>(0);
  const faqs = [{
    question: 'Wat kost hypotheekadvies?',
    answer: 'Wij werken op basis van een vast adviestarief dat vooraf met je wordt besproken. Zo weet je precies waar je aan toe bent. Tijdens het gratis kennismakingsgesprek bespreken we de kosten die bij jouw situatie passen.'
  }, {
    question: 'Hoeveel kan ik maximaal lenen?',
    answer: 'Dit hangt af van je inkomen, vaste lasten en persoonlijke situatie. Tijdens een vrijblijvend gesprek berekenen wij dit voor je. Gemiddeld duurt dit maar 15 minuten.'
  }, {
    question: 'Kan ik mijn hypotheek oversluiten?',
    answer: 'Ja, vaak is oversluiten erg interessant om je maandlasten te verlagen of om overwaarde op te nemen voor een verbouwing of verduurzaming. Wij rekenen graag voor je uit of oversluiten in jouw situatie voordelig is, inclusief eventuele boeterente.'
  }, {
    question: 'Wat is de Nationale Hypotheek Garantie (NHG)?',
    answer: 'NHG biedt een vangnet als je je hypotheek niet meer kunt betalen door bijvoorbeeld werkloosheid of scheiding. Bovendien krijg je met NHG vaak een flinke korting op de hypotheekrente. Wij checken altijd of je in aanmerking komt voor NHG.'
  }, {
    question: 'Hoe lang duurt het hypotheektraject?',
    answer: 'Van eerste gesprek tot sleuteloverdracht duurt gemiddeld 4 tot 8 weken. Wij zorgen dat het proces soepel verloopt en houden je bij elke stap op de hoogte.'
  }, {
    question: 'Wat neem ik mee naar het eerste gesprek?',
    answer: 'Voor het kennismakingsgesprek hoef je niets mee te nemen. Mocht het tot een adviestraject komen, dan vragen wij om: loonstroken (laatste 3 maanden), jaaropgave, ID-bewijs, en eventueel je belastingaangifte als ondernemer.'
  }];
  const timelineSteps = [{
    number: 1,
    title: 'Kennismaking',
    description: 'Een vrijblijvend en kosteloos gesprek waarin wij jouw situatie, wensen en mogelijkheden in kaart brengen.'
  }, {
    number: 2,
    title: 'Vergelijken',
    description: 'Wij vergelijken de voorwaarden van meer dan 30 geldverstrekkers. Niet alleen op rente, maar ook op flexibiliteit en voorwaarden die bij jouw situatie passen.'
  }, {
    number: 3,
    title: 'Advies & Afsluiten',
    description: 'We bespreken het advies en wij regelen de complete afhandeling met de bank. Nul stress voor jou.'
  }];
  return <div className="w-full" data-id="element-524">
      {/* Hero */}
      <PageHero title="Hypotheekadvies op maat" subtitle="Van je eerste starterswoning tot een complexe financiering als ondernemer. Wij maken het overzichtelijk en zoeken de beste voorwaarden bij meer dan 30 geldverstrekkers." label="Hypotheken" data-id="element-525" />

      {/* Target Audience Grid (Bento Box) */}
      <section className="py-20 md:py-28 bg-white" data-id="element-526">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-527">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6" data-id="element-528">
            {/* Starters - Large Card */}
            <FadeIn className="md:col-span-8 bg-warm p-8 md:p-10 rounded-2xl shadow-card border border-gray-100" data-id="element-529">
              <div className="flex items-center mb-6" data-id="element-530">
                <div className="w-12 h-12 bg-navy rounded-full flex items-center justify-center mr-4" data-id="element-531">
                  <UsersIcon className="w-6 h-6 text-white" data-id="element-532" />
                </div>
                <h2 className="text-3xl font-bold text-navy" data-id="element-533">Starters</h2>
              </div>
              <p className="text-gray-600 text-lg leading-relaxed mb-6" data-id="element-534">
                Je eerste huis kopen is spannend. Wij berekenen hoeveel je
                maximaal kunt lenen en kijken verder dan de standaard. We
                onderzoeken ook creatieve opties zoals hulp van ouders via een
                familiebank of schenking, zodat je weet waar je aan toe bent.
              </p>
              <ul className="space-y-3" data-id="element-535">
                <li className="flex items-center text-navy font-medium" data-id="element-536">
                  <CheckCircleIcon className="w-5 h-5 text-gold mr-3" data-id="element-537" />{' '}
                  Berekening hoeveel je maximaal kunt lenen
                </li>
                <li className="flex items-center text-navy font-medium" data-id="element-538">
                  <CheckCircleIcon className="w-5 h-5 text-gold mr-3" data-id="element-539" />{' '}
                  Inclusief check op Nationale Hypotheek Garantie (NHG)
                </li>
                <li className="flex items-center text-navy font-medium" data-id="element-540">
                  <CheckCircleIcon className="w-5 h-5 text-gold mr-3" data-id="element-541" /> Opties
                  voor ouderlijke hulp verkennen
                </li>
                <li className="flex items-center text-navy font-medium" data-id="element-542">
                  <CheckCircleIcon className="w-5 h-5 text-gold mr-3" data-id="element-543" />{' '}
                  Studieschuld? Wij kijken naar je volledige situatie
                </li>
              </ul>
            </FadeIn>

            {/* Ondernemers - Medium Card */}
            <FadeIn delay={0.1} className="md:col-span-4 bg-navy p-8 md:p-10 rounded-2xl shadow-card text-white" data-id="element-544">
              <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mb-6" data-id="element-545">
                <BriefcaseIcon className="w-6 h-6 text-gold" data-id="element-546" />
              </div>
              <h2 className="text-2xl font-bold mb-4" data-id="element-547">Ondernemers</h2>
              <p className="text-gray-300 leading-relaxed" data-id="element-548">
                Geen vast contract? Geen probleem. Wij leveren maatwerk voor
                ZZP'ers en DGA's. Wij weten precies welke banken soepel omgaan
                met wisselende inkomsten en bedrijfsresultaten.
              </p>
            </FadeIn>

            {/* Doorstromers - Medium Card */}
            <FadeIn delay={0.2} className="md:col-span-6 bg-white p-8 md:p-10 rounded-2xl shadow-card border border-gray-100" data-id="element-549">
              <div className="w-14 h-14 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-550">
                <ArrowRightLeftIcon className="w-7 h-7 text-gold" data-id="element-551" />
              </div>
              <h2 className="text-2xl font-bold text-navy mb-4" data-id="element-552">
                Doorstromers
              </h2>
              <p className="text-gray-600 leading-relaxed" data-id="element-553">
                Wij helpen je met het slim inzetten van je overwaarde en het
                meenemen van je gunstige rente. Zo maken we de financiële
                overgang naar je volgende woning zo soepel mogelijk.
              </p>
            </FadeIn>

            {/* Oversluiten & Verduurzamen - Medium Card */}
            <FadeIn delay={0.3} className="md:col-span-6 bg-white p-8 md:p-10 rounded-2xl shadow-card border border-gray-100" data-id="element-554">
              <div className="w-14 h-14 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-555">
                <LeafIcon className="w-7 h-7 text-gold" data-id="element-556" />
              </div>
              <h2 className="text-2xl font-bold text-navy mb-4" data-id="element-557">
                Oversluiten & Verduurzamen
              </h2>
              <p className="text-gray-600 leading-relaxed" data-id="element-558">
                Je maandlasten verlagen of je huis energiezuiniger maken? Wij
                rekenen uit of oversluiten loont en helpen je bij het
                financieren van verduurzaming, inclusief inzicht in subsidies en
                energiebespaarleningen.
              </p>
            </FadeIn>

            {/* Scheidingen - Wide Card */}
            <FadeIn delay={0.4} className="md:col-span-12 bg-white p-8 md:p-10 rounded-2xl shadow-card border border-gray-100 flex flex-col md:flex-row items-center gap-8" data-id="element-559">
              <div className="w-16 h-16 bg-warm rounded-full flex items-center justify-center flex-shrink-0" data-id="element-560">
                <HeartHandshakeIcon className="w-8 h-8 text-gold" data-id="element-561" />
              </div>
              <div data-id="element-562">
                <h2 className="text-2xl font-bold text-navy mb-3" data-id="element-563">
                  Scheidingen & Relatiebreuk
                </h2>
                <p className="text-gray-600 leading-relaxed" data-id="element-564">
                  Een emotionele periode waarin snel duidelijkheid nodig is. Wij
                  zoeken in alle rust en met discretie uit of één van jullie in
                  de huidige woning kan blijven wonen. Wij nemen het financiële
                  traject volledig uit handen, zodat jullie je kunnen focussen
                  op de toekomst.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <ProcessTimeline title="Hoe wij werken" steps={timelineSteps} data-id="element-565" />

      {/* CTA after timeline */}
      <section className="pb-20 md:pb-28 bg-warm" data-id="element-566">
        <div className="text-center" data-id="element-567">
          <FadeIn data-id="element-568">
            <Button variant="primary" size="lg" onClick={() => onNavigate('contact')} data-id="element-569">
              Plan een gratis kennismaking
            </Button>
          </FadeIn>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-20 md:py-28 bg-white" data-id="element-570">
        <div className="max-w-3xl mx-auto px-4 md:px-8" data-id="element-571">
          <SectionHeading title="Veelgestelde vragen" subtitle="De vragen die we het vaakst krijgen." data-id="element-572" />

          <FadeIn delay={0.2} className="bg-warm rounded-2xl shadow-card p-6 md:p-10 border border-gray-100" data-id="element-573">
            {faqs.map((faq, index) => <AccordionItem key={index} question={faq.question} answer={faq.answer} isOpen={openAccordion === index} onClick={() => setOpenAccordion(openAccordion === index ? null : index)} data-id="element-574" />)}
          </FadeIn>
        </div>
      </section>

      {/* Checklist Section */}
      <section className="py-20 md:py-28 bg-navy text-white" data-id="element-575">
        <div className="max-w-5xl mx-auto px-4 md:px-8" data-id="element-576">
          <SectionHeading title="Checklist eerste gesprek" subtitle="Voor een vrijblijvende kennismaking hoef je niets mee te nemen. Gaan we verder met een adviestraject? Dan hebben wij vaak de volgende documenten nodig:" light={true} data-id="element-577" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-578">
            <FadeIn delay={0.1} className="bg-white/10 p-6 rounded-2xl border border-white/20 flex items-center gap-4" data-id="element-579">
              <CreditCardIcon className="w-8 h-8 text-gold flex-shrink-0" data-id="element-580" />
              <span className="font-medium" data-id="element-581">Loonstroken (laatste 3 mnd)</span>
            </FadeIn>
            <FadeIn delay={0.2} className="bg-white/10 p-6 rounded-2xl border border-white/20 flex items-center gap-4" data-id="element-582">
              <FileTextIcon className="w-8 h-8 text-gold flex-shrink-0" data-id="element-583" />
              <span className="font-medium" data-id="element-584">Jaaropgave werkgever</span>
            </FadeIn>
            <FadeIn delay={0.3} className="bg-white/10 p-6 rounded-2xl border border-white/20 flex items-center gap-4" data-id="element-585">
              <UserIcon className="w-8 h-8 text-gold flex-shrink-0" data-id="element-586" />
              <span className="font-medium" data-id="element-587">Geldig ID-bewijs</span>
            </FadeIn>
            <FadeIn delay={0.4} className="bg-white/10 p-6 rounded-2xl border border-white/20 flex items-center gap-4" data-id="element-588">
              <BuildingIcon className="w-8 h-8 text-gold flex-shrink-0" data-id="element-589" />
              <span className="font-medium" data-id="element-590">Werkgeversverklaring</span>
            </FadeIn>
            <FadeIn delay={0.5} className="bg-white/10 p-6 rounded-2xl border border-white/20 flex items-center gap-4" data-id="element-591">
              <CalculatorIcon className="w-8 h-8 text-gold flex-shrink-0" data-id="element-592" />
              <span className="font-medium" data-id="element-593">
                Belastingaangifte (ondernemers)
              </span>
            </FadeIn>
            <FadeIn delay={0.6} className="bg-white/10 p-6 rounded-2xl border border-white/20 flex items-center gap-4" data-id="element-594">
              <HomeIcon className="w-8 h-8 text-gold flex-shrink-0" data-id="element-595" />
              <span className="font-medium" data-id="element-596">Eventueel: koopovereenkomst</span>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Cross-page CTA */}
      <CTASection title="Op zoek naar woonbootfinanciering?" subtitle="Wij zijn gespecialiseerd in het financieren van woonboten. Bekijk onze woonboot-expertise." buttonText="Bekijk onze woonboot-expertise" buttonVariant="secondary" onNavigate={onNavigate} page="woonboten" variant="light" data-id="element-597" />
    </div>;
}